<template>
  <div class="member-source-selector">
    <label :class="required ? 'required' : ''">{{ label }}</label>

    <div
      v-for="option in sourceOptions"
      :key="option.value"
      class="radio-group"
      :class="{ 'is-disabled': isMemberSourceOptionDisabled(option.value) }"
    >
      <label class="radio-label">
        <input
          type="radio"
          v-model="selectedSource"
          :value="option.value"
          class="form-check-input"
          :disabled="isMemberSourceOptionDisabled(option.value)"
        />
        <span>{{ option.label }}</span>
      </label>
      <p v-if="isMemberSourceOptionDisabled(option.value)" class="form-hint disabled-tip">
        {{ getMemberSourceDisabledTip(option.value) }}
      </p>
      <p v-else class="form-hint">{{ option.hint }}</p>
    </div>

    <div v-if="errorMessage" class="invalid-feedback">
      {{ errorMessage }}
    </div>

    <div v-if="showModeLockNotice" class="mode-lock-notice">
      <i class="ri-information-line"></i>
      <span>{{ modeLockNoticeText }}</span>
    </div>

    <div
      v-if="systemTypeHint"
      class="system-type-hint"
      :class="`system-type-hint--${systemTypeHint.type}`"
    >
      <strong v-if="systemTypeHint.type === 'warning'">⚠️ 警告：</strong>
      <span v-else-if="systemTypeHint.type === 'success'">✓ </span>
      <span v-else-if="systemTypeHint.type === 'info'">ℹ️ </span>
      {{ systemTypeHint.text }}
      <template v-if="systemTypeHint.type === 'warning' && currentSourceLabel">
        （目前選擇：{{ currentSourceLabel }}）
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, toRef, watch } from 'vue';
import { useMemberSourceConstraints } from '@/composables/useMemberSourceConstraints.js';

const props = defineProps({
  modelValue: {
    type: String,
    default: 'LOCKLINE'
  },
  clientMemberSystemType: {
    type: String,
    default: null
  },
  label: {
    type: String,
    default: '會員來源'
  },
  required: {
    type: Boolean,
    default: false
  },
  locklineHint: {
    type: String,
    default: '僅允許選擇來源為 LOCKLINE 的會員參與活動'
  },
  externalHint: {
    type: String,
    default: '不判斷會員身份，直接接受並記錄 m 傳來的值'
  },
  wixtarHint: {
    type: String,
    default: '僅允許選擇來源為 Wixtar 的會員參與活動'
  },
  showSystemTypeHint: {
    type: Boolean,
    default: false
  },
  showModeLockNotice: {
    type: Boolean,
    default: false
  },
  modeLockNoticeText: {
    type: String,
    default: '會員模式不能自行變更，如果需要變更請聯絡系統管理員。'
  },
  errorMessage: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['update:modelValue']);

const clientMemberSystemTypeRef = toRef(props, 'clientMemberSystemType');
const {
  isMemberSourceOptionDisabled,
  getMemberSourceDisabledTip,
  enforceMemberSourceConstraints
} = useMemberSourceConstraints(clientMemberSystemTypeRef);

const selectedSource = computed({
  get: () => props.modelValue || 'LOCKLINE',
  set: (value) => emit('update:modelValue', value)
});

const sourceOptions = computed(() => [
  {
    value: 'LOCKLINE',
    label: 'LOCKLINE會員',
    hint: props.locklineHint
  },
  {
    value: 'EXTERNAL',
    label: '允許外部會員',
    hint: props.externalHint
  },
  {
    value: 'WIXTAR',
    label: '啟用Wixtar會員',
    hint: props.wixtarHint
  }
]);

const currentSourceLabel = computed(() => {
  if (selectedSource.value === 'LOCKLINE') return 'LOCKLINE';
  if (selectedSource.value === 'EXTERNAL') return '外部會員';
  if (selectedSource.value === 'WIXTAR') return 'WIXTAR';
  return selectedSource.value || '';
});

const systemTypeHint = computed(() => {
  if (!props.showSystemTypeHint || !props.clientMemberSystemType) {
    return null;
  }

  if (props.clientMemberSystemType === 'WIXTAR' && selectedSource.value !== 'WIXTAR') {
    return {
      type: 'warning',
      text: '此客戶使用 Wixtar 會員系統，建議將會員來源設定為 WIXTAR，否則可能無法正常運作。'
    };
  }

  if (props.clientMemberSystemType === 'LOCKLINE' && selectedSource.value === 'WIXTAR') {
    return {
      type: 'warning',
      text: '此客戶使用 LOCKLINE 會員系統，選擇 WIXTAR 會員來源可能無法正常運作，建議改為 LOCKLINE 或 外部會員。'
    };
  }

  if (props.clientMemberSystemType === 'WIXTAR' && selectedSource.value === 'WIXTAR') {
    return {
      type: 'success',
      text: '此客戶使用 Wixtar 會員系統，會員來源設定正確。'
    };
  }

  if (props.clientMemberSystemType === 'LOCKLINE' && selectedSource.value !== 'WIXTAR') {
    return {
      type: 'info',
      text: '此客戶使用 LOCKLINE 會員系統，當前設定符合要求。'
    };
  }

  return null;
});

const enforceCurrentSource = () => {
  enforceMemberSourceConstraints(selectedSource.value, (nextSource) => {
    selectedSource.value = nextSource;
  });
};

watch(
  () => [props.clientMemberSystemType, selectedSource.value],
  () => {
    enforceCurrentSource();
  },
  { immediate: true }
);
</script>

<style scoped>
.member-source-selector > label {
  display: block;
  margin-bottom: 8px;
}

.member-source-selector > label.required::after {
  content: ' *';
  color: #ef4444;
}

.radio-group {
  margin-bottom: 16px;
}

.radio-group:last-child {
  margin-bottom: 0;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  margin-bottom: 4px;
}

.radio-group.is-disabled {
  opacity: 0.65;
}

.radio-group.is-disabled .radio-label {
  cursor: not-allowed;
}

.form-hint {
  margin: 0;
}

.form-hint.disabled-tip {
  color: #856404;
  background-color: #fff3cd;
  padding: 6px 8px;
  border-radius: 4px;
  border-left: 3px solid #ffc107;
  margin-top: 4px;
}

.invalid-feedback {
  display: block;
  margin-top: 8px;
}

.mode-lock-notice {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 8px;
  padding: 10px 12px;
  border: 1px solid #ffe69c;
  border-left: 4px solid #ffb100;
  border-radius: 4px;
  background: #fff8db;
  color: #7a4e00;
  font-size: 13px;
  line-height: 1.5;
  font-weight: 500;
}

.mode-lock-notice i {
  font-size: 16px;
  margin-top: 1px;
  color: #c88600;
}

.system-type-hint {
  margin-top: 8px;
  padding: 8px;
  border-left: 4px solid;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.5;
}

.system-type-hint--warning {
  color: #856404;
  background: #fff3cd;
  border-left-color: #ffc107;
}

.system-type-hint--success {
  color: #155724;
  background: #d1e7dd;
  border-left-color: #28a745;
}

.system-type-hint--info {
  color: #0c5460;
  background: #d1ecf1;
  border-left-color: #17a2b8;
}
</style>
